import type { GeneralStatus } from './equipment';
import type { HabilitationStatus } from './habilitation';
import type { MedicalVisitStatus } from './medical-visit';
import type { Organization } from './organization';
import type { Tag } from './tag';
import type { TrainingStatus } from './training';

export const ActivationStatus = {
  ACTIVE: 'ACTIVE',
  PENDING: 'PENDING',
  NO_ACCOUNT: 'NO_ACCOUNT',
  DEACTIVATED: 'DEACTIVATED',
} as const;

export type ActivationStatus =
  (typeof ActivationStatus)[keyof typeof ActivationStatus];

export const Role = {
  NONE: 'NONE',
  HOLDER: 'HOLDER',
  READER: 'READER',
  REVIEWER: 'REVIEWER',
  EDITOR: 'EDITOR',
  MANAGER: 'MANAGER',
  ADMIN: 'ADMIN',
  SUPERADMIN: 'SUPERADMIN',
} as const;

export type Role = (typeof Role)[keyof typeof Role];

export const AutorizedRole = {
  NONE: 'NONE',
  HOLDER: 'HOLDER',
  READER: 'READER',
  REVIEWER: 'REVIEWER',
  EDITOR: 'EDITOR',
  MANAGER: 'MANAGER',
  ADMIN: 'ADMIN',
} as const;

export type AutorizedRole = (typeof AutorizedRole)[keyof typeof AutorizedRole];

export const USER_COMPLIANCE_STATUS_VALUES = [
  'CONFORM',
  'NOT_CONFORM',
  'NOTIFY',
  'NA',
] as const;

export type UserComplianceStatus =
  (typeof USER_COMPLIANCE_STATUS_VALUES)[number];

export type UserEquipmentComplianceSourceStatus = GeneralStatus;

export type UserTrainingComplianceSourceStatus = TrainingStatus;

// 'MISSING' = a mandatory MedicalVisitType with no MedicalVisit at all for
// this user yet (distinct from an existing visit that expired). Only ever
// emitted when the type's initialDeadlineMonths is set — otherwise a user
// with zero visits of that type yields no status at all (see
// UserComplianceService), so orgs aren't forced to backfill historical
// visits for existing employees. Maps to NOTIFY severity, not NOT_CONFORM
// (see computeUserMedicalVisitStatus).
// 'INAPTE' = the latest visit's physician opinion is "inapte", which
// overrides the date-based MedicalVisitStatus: an inaptitude declaration
// requires immediate action (reclassement or dismissal) regardless of how
// far away the next visit is due — see UserComplianceService. Maps to
// NOT_CONFORM.
export type UserMedicalVisitComplianceSourceStatus =
  | MedicalVisitStatus
  | 'MISSING'
  | 'INAPTE';

// No 'MISSING' counterpart: unlike medical visit types, an unheld
// HabilitationType never counts against a user — see HabilitationType.
export type UserHabilitationComplianceSourceStatus = HabilitationStatus;

export type LoginOptions = {
  hasAccount: boolean;
  emailSignIn: boolean;
  passwordSignIn: boolean;
  googleSignIn: boolean;
  appleSignIn: boolean;
};

export type BaseUser = {
  id: number;
  createdAt: Date;
  email: string | null;
  firstName: string;
  lastName: string;
  role: Role;
  phone?: string | null;
  comment: string | null;
  /* true when the user can sign in with a password (never the hash) */
  hasPassword?: boolean;
  /**
   * @deprecated legacy flag, "true" when a password is set; read hasPassword
   * first (see `userHasPassword`). Only still sent by API builds that predate
   * hasPassword, so a client cannot assume either field during a rolling deploy.
   */
  password?: string | null;
  /* set when the user can sign in with Google / Apple (used to know whether they need a password) */
  googleId?: string | null;
  appleId?: string | null;
  externalUserId: string | null;
  activationStatus: ActivationStatus;
  status: UserComplianceStatus;
  trainingStatus: UserComplianceStatus;
  equipmentStatus: UserComplianceStatus;
  medicalVisitStatus: UserComplianceStatus;
  habilitationStatus: UserComplianceStatus;
  isDeactivated: boolean;
  shouldReceiveEmailPreference: boolean;
  isValidated: boolean;
  language: string | null;
  /**
   * IANA zone name, reported by the browser rather than chosen in a form.
   * Null until a client has sent one; readers fall back to Europe/Paris.
   * Optional for the same reason as `avatar`: narrow Prisma selects do not
   * project it, and no view breaks when it is absent.
   */
  timezone?: string | null;
  /* R2 object path of the user's avatar, or null to fall back to initials.
     Optional because narrow Prisma selects don't always project it — the UI
     falls back to initials, so a missing field is never a broken render. */
  avatar?: string | null;
  tags?: Tag[];
  Organization: {
    id: number;
    name: string;
  };
};

export type User = BaseUser & {
  Organization: Organization;
};

export type UserEmailValidationResponse = {
  email: string;
};
